import { useMemo } from 'react';
import { Box } from '@nimbus-ds/components';
import { EyeOffIcon } from '@nimbus-ds/icons';
import { InteractiveList } from '@nimbus-ds/patterns';
import { CurrencyType } from '@tiendanube/common';
import { Label } from '@tiendanube/components';
import { ImageItemStratus, useResponsive } from 'commons/components';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import { VariantList } from './VariantList';
import { ProductInterface, SelectedProductInterface } from '../../types';
import { VariantStockPrice } from '../VariantStockPrice';

interface ProductListItemProps<Minimalistic extends boolean> {
  variants: ProductInterface<false>[];
  selectedVariants: SelectedProductInterface[];
  minimalistic?: boolean;
  selectedVariantsLimitReached?: boolean;
  currencyDisplay?: CurrencyType;
  showPrice: boolean;
  allowInfiniteStockSelection: boolean;
  onChange: (
    selectedProducts: ProductInterface<Minimalistic>[],
    checked: boolean,
  ) => void;
}

function ProductListItem<Minimalistic extends boolean>({
  variants,
  onChange,
  selectedVariants,
  minimalistic,
  selectedVariantsLimitReached,
  currencyDisplay,
  showPrice,
  allowInfiniteStockSelection,
}: Readonly<ProductListItemProps<Minimalistic>>) {
  const t = useTranslationOrders();
  const { isMobile } = useResponsive();

  const productId = variants[0].productId;
  const productName = variants[0].name;
  const productImageUrl = variants[0].imageUrl;
  const isHidden = !variants[0].visible;

  const isChecked = useMemo(
    () =>
      selectedVariants.length > 0 &&
      selectedVariants.length ===
        variants.filter(
          (v) =>
            minimalistic ||
            (v.stock !== 0 &&
              ((v.stock !== null && !allowInfiniteStockSelection) ||
                allowInfiniteStockSelection)),
        ).length,
    [selectedVariants, variants, minimalistic, allowInfiniteStockSelection],
  );

  const isIndeterminate = useMemo(
    () => selectedVariants.filter((v) => !v.isPreselected).length > 0,
    [selectedVariants],
  );

  const hasDefaultVariant = useMemo(
    () =>
      variants.length === 1 && !Object.keys(variants[0].variantValues).length,
    [variants],
  );

  const preSelectedVariants = selectedVariants.filter((sv) => sv.isPreselected);
  const variantSelectables = variants
    .filter((v) => v.stock !== 0)
    .filter(
      (v) =>
        (v.stock !== null && !allowInfiniteStockSelection) ||
        allowInfiniteStockSelection,
    );

  const isDisabled = useMemo(
    () =>
      (!minimalistic &&
        ((hasDefaultVariant &&
          (variants[0].stock === 0 ||
            (variants[0].stock === null && !allowInfiniteStockSelection) ||
            selectedVariants[0]?.isPreselected)) ||
          preSelectedVariants.length === variantSelectables.length)) ||
      (selectedVariantsLimitReached && !isChecked && !isIndeterminate),
    [
      allowInfiniteStockSelection,
      hasDefaultVariant,
      isChecked,
      isIndeterminate,
      minimalistic,
      preSelectedVariants.length,
      selectedVariants,
      selectedVariantsLimitReached,
      variants,
      variantSelectables.length,
    ],
  );

  const handleOnChange = (checked: boolean) => {
    const variantsToModify = checked
      ? variants.filter(
          (v) =>
            (minimalistic ||
              (allowInfiniteStockSelection && v.stock === null) ||
              (v.stock !== null && v.stock > 0)) &&
            !selectedVariants.find((sv) => sv.variantId === v.variantId),
        )
      : variants.filter((v) =>
          selectedVariants
            .filter((sv) => !sv.isPreselected)
            .find((sv) => sv.variantId === v.variantId),
        );

    onChange(variantsToModify, checked);
  };

  return (
    <InteractiveList.CheckboxItem
      title=""
      showTitle={false}
      topDivider={false}
      checkbox={{
        name: 'checkbox-element',
        onChange: ({ currentTarget }) => handleOnChange(currentTarget.checked),
        indeterminate: isIndeterminate,
        checked: isChecked,
        disabled: isDisabled,
      }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box flex="3">
          <ImageItemStratus
            id={productId}
            title={productName}
            imageUrl={productImageUrl}
            imageWidth="56px"
            appearance="default"
            titleAppearance="neutral-textLow"
            titleSize="base"
            bold
          >
            {isHidden && (
              <Box paddingY="1">
                <Label
                  id={productId}
                  label={t('productSearcher.hiddenItem')}
                  appearance="warning"
                  icon={EyeOffIcon}
                />
              </Box>
            )}
            {!hasDefaultVariant && (
              <VariantList
                variants={variants}
                selectedVariants={selectedVariants}
                minimalistic={minimalistic}
                selectedVariantsLimitReached={selectedVariantsLimitReached}
                onChange={onChange}
                currencyDisplay={currencyDisplay}
                showPrice={showPrice}
                allowInfiniteStockSelection={allowInfiniteStockSelection}
              />
            )}
          </ImageItemStratus>
        </Box>
        {!minimalistic && hasDefaultVariant && (
          <Box flex={isMobile ? '3' : '2'} paddingLeft="2">
            <VariantStockPrice
              variant={variants[0]}
              showPrice={showPrice}
              currencyDisplay={currencyDisplay}
            />
          </Box>
        )}
      </Box>
    </InteractiveList.CheckboxItem>
  );
}

export default ProductListItem;
